const API_BASE = import.meta.env.VITE_API_URL || '';
const GRAPHQL_URL = import.meta.env.VITE_GRAPHQL_URL || `${API_BASE}/graphql`;

const resolveTenantCode = (tenant) =>
  tenant?.code || tenant?.codigo || tenant?.tenantCode || tenant?.id || tenant?.nombre || '';

const buildHeaders = ({ tenant, token } = {}) => {
  const headers = {
    'Content-Type': 'application/json',
  };
  const tenantCode = resolveTenantCode(tenant);
  if (tenantCode) {
    headers['X-Tenant'] = tenantCode;
  }
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
};

const handleResponse = async (response, fallbackMessage) => {
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload.ok === false) {
    const message = payload?.message || payload?.error || fallbackMessage;
    const error = new Error(message);
    error.payload = payload;
    error.status = response.status;
    throw error;
  }
  return payload.data;
};

async function gqlRequest({ tenant, token, query, variables = {}, fallbackMessage }) {
  const response = await fetch(GRAPHQL_URL, {
    method: 'POST',
    headers: buildHeaders({ tenant, token }),
    body: JSON.stringify({ query, variables }),
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload.errors?.length) {
    const message = payload?.errors?.[0]?.message || fallbackMessage;
    throw new Error(message);
  }

  return payload.data;
}

export async function valorizarGuia({ tenant, token, guiaId, payload }) {
  // Flujo oficial de valorización/revalorización de guía:
  // mantener integraciones sobre /api/tarifas/guias/:id/* y no sobre /api/guias/:id/valorizar.
  const response = await fetch(`${API_BASE}/api/tarifas/guias/${guiaId}/revalorizar`, {
    method: 'POST',
    headers: buildHeaders({ tenant, token }),
    body: JSON.stringify(payload),
  });
  return handleResponse(response, 'No se pudo valorizar la guía.');
}

export async function previewRevalorizacionGuia({ tenant, token, guiaId, payload }) {
  // Flujo oficial de preview de revalorización.
  const response = await fetch(`${API_BASE}/api/tarifas/guias/${guiaId}/revalorizar/preview`, {
    method: 'POST',
    headers: buildHeaders({ tenant, token }),
    body: JSON.stringify(payload),
  });
  return handleResponse(response, 'No se pudo previsualizar la revalorización de la guía.');
}

export async function fetchConceptosValorizacion({ tenant, token }) {
  const data = await gqlRequest({
    tenant,
    token,
    query: `query { conceptosValorizacion { id codigo nombre tipo aplicaSobre activo orden valor formula } }`,
    fallbackMessage: 'No se pudieron cargar los conceptos de valorización.',
  });
  return data.conceptosValorizacion || [];
}

export async function saveConceptoValorizacion({ tenant, token, id, input }) {
  const op = id
    ? {
      query: `mutation($id: ID!, $input: ConceptoValorizacionInput!) { updateConcepto(id: $id, input: $input) { id codigo nombre tipo aplicaSobre activo orden valor formula } }`,
      variables: { id, input },
      field: 'updateConcepto',
    }
    : {
      query: `mutation($input: ConceptoValorizacionInput!) { createConcepto(input: $input) { id codigo nombre tipo aplicaSobre activo orden valor formula } }`,
      variables: { input },
      field: 'createConcepto',
    };
  const data = await gqlRequest({ tenant, token, ...op, fallbackMessage: 'No se pudo guardar el concepto.' });
  return data[op.field];
}

export async function fetchTarifasClienteValorizacion({ tenant, token, clienteId, lineaId }) {
  const data = await gqlRequest({
    tenant,
    token,
    query: `query($clienteId: ID!, $lineaId: String) { tarifasCliente(clienteId: $clienteId, lineaId: $lineaId) { id clienteId lineaId zonaId kgDesde kgHasta precioBase minimo activo createdAt } }`,
    variables: { clienteId, lineaId: lineaId || null },
    fallbackMessage: 'No se pudieron cargar las tarifas de clientes.',
  });
  return data.tarifasCliente || [];
}

export async function createTarifaClienteValorizacion({ tenant, token, input }) {
  const data = await gqlRequest({
    tenant,
    token,
    query: `mutation($input: TarifaClienteInput!) { createTarifaCliente(input: $input) { id clienteId lineaId zonaId kgDesde kgHasta precioBase minimo activo createdAt } }`,
    variables: { input },
    fallbackMessage: 'No se pudo crear la tarifa cliente.',
  });
  return data.createTarifaCliente;
}

export async function fetchTarifasFleteroValorizacion({ tenant, token }) {
  const data = await gqlRequest({
    tenant,
    token,
    query: `query { tarifasFletero { id fleteroId lineaId tipoCalculo valor activo createdAt } }`,
    fallbackMessage: 'No se pudieron cargar las tarifas de fleteros.',
  });
  return data.tarifasFletero || [];
}

export async function createTarifaFleteroValorizacion({ tenant, token, input }) {
  const data = await gqlRequest({
    tenant,
    token,
    query: `mutation($input: TarifaFleteroInput!) { createTarifaFletero(input: $input) { id fleteroId lineaId tipoCalculo valor activo createdAt } }`,
    variables: { input },
    fallbackMessage: 'No se pudo crear la tarifa de fletero.',
  });
  return data.createTarifaFletero;
}

export async function recalcularGuiaValorizacion({ tenant, token, guiaId }) {
  const data = await gqlRequest({
    tenant,
    token,
    query: `mutation($guiaId: ID!) { recalcularGuia(guiaId: $guiaId) { guiaId totalFlete totalConceptos totalFinal margenEstimado conceptosJson conceptos { conceptoId codigo nombre tipo aplicaSobre base valor importe } } }`,
    variables: { guiaId },
    fallbackMessage: 'No se pudo recalcular la guía.',
  });
  return data.recalcularGuia;
}
